
import { useState, useEffect } from 'react';
import { formatInAlbania } from '@/lib/date-utils';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { DateRange } from 'react-day-picker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format, formatDistanceStrict } from 'date-fns';
import { cn } from '@/lib/utils';
import { getCheckInOutRecords } from '@/api/dashboard';
import { useQuery } from '@tanstack/react-query';
import { useGym } from '@/contexts/gym-context';
import { DataTablePagination } from '@/components/ui/data-table-pagination';
import { TableEmptyRow, TableLoadingRows } from '@/components/shared/table-states';

interface CheckInTableProps {
  refreshKey?: number;
}

export function CheckInTable({ refreshKey }: CheckInTableProps) {
  const { t } = useTranslation();
  const { selectedGym } = useGym();
  const [dateRange, setDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: new Date(),
    to: new Date(),
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Format dates for API call (DD-MM-YYYY)
  const formatDateForAPI = (date: Date) => {
    return format(date, 'dd-MM-yyyy');
  };

  const {
    data: checkInOutRecords = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      'check-in-out-records',
      formatDateForAPI(dateRange.from),
      formatDateForAPI(dateRange.to),
      selectedGym,
    ],
    queryFn: () =>
      getCheckInOutRecords(
        formatDateForAPI(dateRange.from),
        formatDateForAPI(dateRange.to),
        selectedGym === 'all' ? undefined : selectedGym
      ),
    enabled: !!dateRange.from && !!dateRange.to,
  });

  // Refetch when refreshKey or selectedGym changes
  useEffect(() => {
    if (refreshKey !== undefined || selectedGym) {
      refetch();
    }
  }, [refreshKey, selectedGym, refetch]);

  // Sort records by check-in time (most recent first)
  const sortedRecords = [...checkInOutRecords].sort(
    (a, b) => new Date(b.checkIn).getTime() - new Date(a.checkIn).getTime()
  );

  // Pagination calculations
  const totalItems = sortedRecords.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRecords = sortedRecords.slice(startIndex, endIndex);

  // Reset to first page when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [checkInOutRecords, itemsPerPage]);

  // Calculate duration between check-in and check-out
  const calculateDuration = (checkIn: string, checkOut: string | null) => {
    if (!checkOut) return '-';
    return formatDistanceStrict(new Date(checkOut), new Date(checkIn));
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (!range) return;

    if (range.from && range.to) {
      setDateRange({
        from: range.from,
        to: range.to,
      });
    } else if (range.from) {
      setDateRange({
        from: range.from,
        to: range.from,
      });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="space-y-4">
      {/* Date Range Picker */}
      <div className="flex items-center gap-4">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'w-full justify-start text-left font-normal sm:w-[300px]',
                !dateRange && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    {format(dateRange.from, 'LLL dd, y')} -{' '}
                    {format(dateRange.to, 'LLL dd, y')}
                  </>
                ) : (
                  format(dateRange.from, 'LLL dd, y')
                )
              ) : (
                <span>Pick a date range</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={dateRange?.from}
              selected={dateRange}
              onSelect={handleDateRangeChange}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('checkIn.user')}</TableHead>
            <TableHead>{t('checkIn.gym')}</TableHead>
            <TableHead>{t('checkIn.checkInTime')}</TableHead>
            <TableHead>{t('checkIn.checkOutTime')}</TableHead>
            <TableHead>{t('checkIn.duration')}</TableHead>
            <TableHead>{t('checkIn.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableLoadingRows colSpan={6} />
          ) : sortedRecords.length === 0 ? (
            <TableEmptyRow
              colSpan={6}
              message={
                selectedGym && selectedGym !== 'all'
                  ? t(
                      'checkIn.noCheckInsForGym',
                      'No check-ins found for the selected gym in this date range'
                    )
                  : t('checkIn.noCheckIns')
              }
            />
          ) : (
            paginatedRecords.map((record) => (
              <TableRow key={record._id}>
                <TableCell className="font-medium">
                  {record.userId?.name || 'Unknown'} {record.userId?.surname || 'User'}
                </TableCell>
                <TableCell>{record.gymId.name}</TableCell>
                <TableCell>
                  {formatInAlbania(record.checkIn, 'DD-MM-YYYY HH:mm')}
                </TableCell>
                <TableCell>
                  {record.checkOut
                    ? formatInAlbania(record.checkOut, 'DD-MM-YYYY HH:mm')
                    : '-'}
                </TableCell>
                <TableCell>
                  {calculateDuration(record.checkIn, record.checkOut)}
                </TableCell>
                <TableCell>
                  <Badge variant={record.checkOut ? 'secondary' : 'default'}>
                    {record.checkOut
                      ? t('checkIn.completed')
                      : t('checkIn.active')}
                  </Badge>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {!isLoading && (
        <DataTablePagination
          page={currentPage}
          pageCount={totalPages}
          total={totalItems}
          pageSize={itemsPerPage}
          onPageChange={handlePageChange}
          onPageSizeChange={(n) => {
            setItemsPerPage(n);
            setCurrentPage(1);
          }}
          pageSizeOptions={[5, 10, 20, 50, 100]}
        />
      )}
    </div>
  );
}
